<template>
  <div class="bed-visualizer-review bed-visualizer__page">
    <div class="bed-visualizer__head">
      <badge
        v-if="badge"
        block-class="bed-visualizer"
        :label="badge"
      />

      <h1
        class="bed-visualizer__title h6"
        v-text="title"
      />

      <p
        class="body-2"
        v-text="description"
      />

      <div
        v-if="enableSave"
        class="bed-visualizer__actions"
      >
        <div class="bed-visualizer__action">
          <button
            class="bed-visualizer__action-btn"
            disabled
            name="save"
            v-html="$icon(`enclosed.heartMedium`)"
          />
        </div>

        <div class="bed-visualizer__action">
          <button
            class="bed-visualizer__action-btn"
            disabled
            name="share"
            v-html="$icon(`enclosed.saveMedium`)"
          />
        </div>
      </div>
    </div>

    <div
      ref="scrollElement"
      class="bed-visualizer__scroll"
    >
      <div class="bed-visualizer__products">
        <template
          v-for="page in customizePageOrder"
          :key="page"
        >
          <div
            v-if="config.selectedOptions[page] && config.selectedOptions[page].length"
            class="bed-visualizer__product-group"
          >
            <div>
              <div class="bed-visualizer__product-group-head">
                <div
                  class="bed-visualizer__product-group-title body-1"
                  v-text="page"
                />

                <button
                  class="bed-visualizer__button bed-visualizer__button-edit"
                  @click="handleEditClick(page)"
                >
                  <span
                    class="bed-visualizer__button-text bed-visualizer__button-text-edit caption"
                    v-text="$string('bedVisualizer.review.edit')"
                  />

                  <span v-html="$icon('misc.edit')" />
                </button>
              </div>

              <template
                v-for="(variant, index) in config.selectedOptions[page]"
                :key="variant.id"
              >
                <product-chip
                  v-if="!variant.isAddOn"
                  :block-class="'bed-visualizer'"
                  :image="variant.images?.thumbnail"
                  :index="index"
                  :is-active="true"
                  :product-title="variant.productTitle"
                  :show-price="true"
                  :variant="variant"
                />
              </template>
            </div>

            <div
              v-if="addOns[page] && addOns[page].variants.length > 0"
              class="bed-visualizer__addons"
            >
              <div
                v-if="addOns[page].label"
                class="body-2 bed-visualizer__addon-label"
                v-text="addOns[page].label"
              />
              <product-chip
                v-for="(variant, index) in addOns[page].variants"
                :key="'addon-' + variant.id"
                :image="variant.images?.thumbnail"
                :image-ratio="'128:48'"
                :index="index"
                :is-active="getAddOnIndex(page, variant) > -1"
                :product-title="variant.productTitle"
                :show-checkbox="true"
                :show-price="true"
                :small="true"
                :variant="variant"
                class="bed-visualizer__addon"
                @click="handleAddOnClick(page, variant)"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Bed visualizer review (bed-visualizer-review)
 * -----------------------------------------------------------------------------
 * Displays contents of bed visualizer review page.
 *
 * @param {Array} customizePageOrder - Order of customize pages.
 */
import { mapState } from 'vuex'

import Badge from '~global/badge/badge'
import ProductChip from '../product-chip'
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'

export default {
  name: 'BedVisualizerReview',

  components: {
    Badge,
    ProductChip,
  },

  props: {
    addOns: {
      type: Object,
      default: () => ({}),
    },
    customizePageOrder: {
      type: Array,
      default: () => [],
      require: true,
    },
    enableSave: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['option-change', 'option-edit'],

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      config: (state) => state['bed-visualizer'].config,
    }),

    /**
     * Compute the badge.
     */
    badge() {
      return this.config.activeFlow === 'custom'?
        this.$string('bedVisualizer.review.badge') :
        ''
    },

    /**
     * Compute the description.
     */
    description() {
      return this.config.activeFlow === 'custom'?
        this.$string('bedVisualizer.review.description') :
        this.$string('bedVisualizer.collection.review_subtitle')
    },

    /**
     * Compute the title.
     */
    title() {
      return this.config.activeFlow === 'custom' ?
        this.$string('bedVisualizer.review.title') :
        this.config.selectedCollection?.activeCollection?.name
    },
  },
  mounted() {
    disableBodyScroll(this.$refs.scrollElement)
  },
  unmounted() {
    clearAllBodyScrollLocks()
  },
  methods: {

    /**
     * Handle edit.
     */
    handleEditClick(page) {
      this.$emit('option-edit', page)
    },

    /**
     * Get the index of the add-on in existing config.
     * @param {String} category - Category name
     * @param {Object} variant - Formatted variant that is used in the bed
     * visualizer.
     * @returns {boolean|*}
     */
    getAddOnIndex(category, variant) {
      return this.config.selectedOptions[category]?.findIndex(
        option => option.id === variant.id && option.isAddOn === true,
      )
    },

    /**
     * Emit option change when add-on is clicked
     * @param {String} category - Category name.
     * @param {Object} variant - Formatted variant that is used in the bed
     * visualizer.
     */
    handleAddOnClick(category, variant) {
      const selectedIndex = this.getAddOnIndex(category, variant)
      if (!this.config.selectedOptions[category]) {
        this.config.selectedOptions[category] = []
      }
      const selectedOptions = [...this.config.selectedOptions[category]]

      if (selectedIndex > -1) {
        selectedOptions.splice(selectedIndex, 1)
      } else {
        selectedOptions.push(variant)
      }

      this.$emit('option-change', {
        name: category,
        selectedOptions,
      })
    },
  },
}
</script>

<style lang="scss">
@import './bed-visualizer.review';
</style>
